import { Button, Card, Form, Input, Row, Typography, notification } from "antd";
import { useMutation } from "@tanstack/react-query";
import { updatePassword } from "../../services/auth";
import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { validatePassword } from "../../utils";

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

// Updated payload type
export type UpdatePasswordPayloadTypes = {
  password: string;
  confirmPassword: string;
};

export const UpdatePasswordModal = () => {
  const [form] = Form.useForm();

  const [allValues, setAllValues] = useState<{
    password?: string;
    confirmPassword?: string;
  }>();

  const { processLogout } = useAuth();

  const { mutate, isPending } = useMutation({
    mutationKey: ["updatePassword"],
    mutationFn: updatePassword,

    onSuccess: (res: any) => {
  if (res.status === 200) {
    notification.success({
      message: "Success",
      description:
        res?.data?.message || "Password reset successfully.",
    });

    processLogout();
  }
},

onError: (error: any) => {
  notification.error({
    message: "Error",
    description:
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Something went wrong",
  });
}
  });

  const payload: UpdatePasswordPayloadTypes = {
    password: allValues?.password || "",
    confirmPassword: allValues?.confirmPassword || "",
  };

  const isBtnDisabled =
    allValues?.confirmPassword !== allValues?.password ||
    !allValues?.password ||
    !allValues?.confirmPassword;

  return (
    <div className="login-page flex h-screen items-center justify-center">
      <Card
        title="Update Password"
        extra={<Row></Row>}
        style={{ maxWidth: 600, width: "100%" }}
      >
        <Row className="pb-2 flex-wrap w-100 pl-6 text-start">
          <Typography.Text type="secondary">
            Your password must be at least 12 characters long and should have
            at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1
            symbol.
          </Typography.Text>
        </Row>

        <Form
          {...formItemLayout}
          form={form}
          name="update-password"
          onFinish={() => mutate(payload)}
          initialValues={{}}
          style={{ maxWidth: 600 }}
          scrollToFirstError
          onValuesChange={(changedValues, values) => {
            setAllValues(values);
          }}
        >
          <Form.Item
            name="password"
            label="Password"
            hasFeedback
            rules={[
              {
                required: true,
                message: "Please input your password!",
              },
              {
                validator: validatePassword,
              },
            ]}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={["password"]}
            hasFeedback
            rules={[
              {
                required: true,
                message: "Please confirm your password!",
              },

              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error(
                      "The new password that you entered do not match!"
                    )
                  );
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm password" />
          </Form.Item>

          <Form.Item className="flex justify-end">
            <Button
              type="primary"
              htmlType="submit"
              loading={isPending}
              disabled={isBtnDisabled}
            >
              Update Password
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};